import { useState, useEffect } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { api } from "@/lib/api";
import type { User, Booking, Tool } from "@/lib/types";
import { Header } from "@/components/Header";
import { DiscoverPage } from "@/components/DiscoverPage";
import { ToolDetail } from "@/components/ToolDetail";
import { ListToolPage } from "@/components/ListToolPage";
import { BookingsPage } from "@/components/BookingsPage";
import { ProfilePage } from "@/components/ProfilePage";
import { MyToolsPage } from "@/components/MyToolsPage";
import { BookingConfirmDialog } from "@/components/BookingConfirmDialog";
import { RatingDialog } from "@/components/RatingDialog";
import { DamageReportDialog } from "@/components/DamageReportDialog";
import "./index.css";

type View =
	| { type: "discover" }
	| { type: "tool-detail"; toolId: string }
	| { type: "list-tool" }
	| { type: "bookings" }
	| { type: "my-tools" }
	| { type: "profile"; userId?: string };

type EnrichedBooking = Booking & { tool?: Tool; borrower?: User; lender?: User };

export function App() {
	const [currentUser, setCurrentUser] = useState<User | null>(null);
	const [view, setView] = useState<View>({ type: "discover" });

	const [bookingDialog, setBookingDialog] = useState<{
		open: boolean;
		toolId: string;
		startDate: string;
		endDate: string;
	}>({ open: false, toolId: "", startDate: "", endDate: "" });

	const [ratingDialog, setRatingDialog] = useState<{
		open: boolean;
		booking: EnrichedBooking | null;
	}>({ open: false, booking: null });

	const [damageDialog, setDamageDialog] = useState<{
		open: boolean;
		bookingId: string;
	}>({ open: false, bookingId: "" });

	useEffect(() => {
		api.getMe().then(setCurrentUser).catch(console.error);
	}, []);

	function navigate(viewName: string) {
		switch (viewName) {
			case "discover":
				setView({ type: "discover" });
				break;
			case "my-tools":
				setView({ type: "my-tools" });
				break;
			case "bookings":
				setView({ type: "bookings" });
				break;
			case "profile":
				setView({ type: "profile" });
				break;
			case "list-tool":
				setView({ type: "list-tool" });
				break;
		}
	}

	const currentViewName = (() => {
		switch (view.type) {
			case "discover":
			case "tool-detail":
				return "discover";
			case "list-tool":
			case "my-tools":
				return "my-tools";
			case "bookings":
				return "bookings";
			case "profile":
				return "profile";
		}
	})();

	return (
		<TooltipProvider>
			<div className="min-h-screen bg-background">
				<Header
					currentUser={currentUser}
					currentView={currentViewName}
					onNavigate={navigate}
				/>

				<main className="max-w-7xl mx-auto px-4 py-6">
					{view.type === "discover" && (
						<DiscoverPage
							onSelectTool={(toolId) =>
								setView({ type: "tool-detail", toolId })
							}
						/>
					)}

					{view.type === "tool-detail" && (
						<ToolDetail
							toolId={view.toolId}
							onBack={() => setView({ type: "discover" })}
							onBook={(toolId, start, end) =>
								setBookingDialog({
									open: true,
									toolId,
									startDate: start,
									endDate: end,
								})
							}
							onViewProfile={(userId) =>
								setView({ type: "profile", userId })
							}
						/>
					)}

					{view.type === "list-tool" && (
						<ListToolPage
							onBack={() => setView({ type: "my-tools" })}
							onSuccess={(toolId) =>
								setView({ type: "tool-detail", toolId })
							}
						/>
					)}

					{view.type === "bookings" && currentUser && (
						<BookingsPage
							currentUserId={currentUser.id}
							onViewTool={(toolId) =>
								setView({ type: "tool-detail", toolId })
							}
							onReportDamage={(bookingId) =>
								setDamageDialog({ open: true, bookingId })
							}
							onRateUser={(booking) =>
								setRatingDialog({ open: true, booking })
							}
						/>
					)}

					{view.type === "my-tools" && currentUser && (
						<MyToolsPage
							currentUserId={currentUser.id}
							onViewTool={(toolId) =>
								setView({ type: "tool-detail", toolId })
							}
							onListTool={() => setView({ type: "list-tool" })}
						/>
					)}

					{view.type === "profile" && (
						<ProfilePage
							userId={view.userId}
							isOwnProfile={!view.userId || view.userId === currentUser?.id}
							onBack={
								view.userId
									? () => setView({ type: "discover" })
									: undefined
							}
							onViewTool={(toolId) =>
								setView({ type: "tool-detail", toolId })
							}
						/>
					)}
				</main>

				<BookingConfirmDialog
					open={bookingDialog.open}
					onClose={() =>
						setBookingDialog({ open: false, toolId: "", startDate: "", endDate: "" })
					}
					toolId={bookingDialog.toolId}
					startDate={bookingDialog.startDate}
					endDate={bookingDialog.endDate}
					onSuccess={() => setView({ type: "bookings" })}
				/>

				{ratingDialog.booking && currentUser && (
					<RatingDialog
						open={ratingDialog.open}
						onClose={() => setRatingDialog({ open: false, booking: null })}
						booking={ratingDialog.booking}
						currentUserId={currentUser.id}
					/>
				)}

				<DamageReportDialog
					open={damageDialog.open}
					onClose={() => setDamageDialog({ open: false, bookingId: "" })}
					bookingId={damageDialog.bookingId}
				/>
			</div>
		</TooltipProvider>
	);
}

export default App;
